import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Reusable Dialog Component
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback for closing the dialog
 * @param {string} title - Dialog title (default: "Confirm Action")
 * @param {string|React.ReactNode} content - Dialog content text or JSX (default: "Are you sure you want to proceed?")
 * @param {string} cancelText - Cancel button text (default: "Cancel")
 * @param {string} confirmText - Confirm button text (default: "Submit")
 * @param {function} onCancel - Callback for cancel action (default: onClose)
 * @param {function} onConfirm - Callback for confirm action (required if you want the confirm button to do something)
 * @param {string} confirmColor - Color of confirm button (default: "primary")
 * @param {boolean} showCloseIcon - Show X icon in top right (default: true)
 * @param {boolean} showDialogActions - Show the Dialog Actions (default: true)
 * @param {boolean} showDialogTitle - Show the Dialog Actions (default: true)
 * @param {string} maxWidth - Dialog max width (default: "sm")
 * @param {boolean} fullWidth - Full width dialog (default: true)
 * @param {boolean} disableConfirm - Disable confirm button (default: false)
 * @param {boolean} disableCancel - Disable cancel button (default: false)
 * @param {boolean} hideCancel - Hide cancel button (default: false)
 * @param {boolean} hideConfirm - Hide confirm button (default: false)
 * @param {boolean} confirmLoading - Show loading spinner on confirm button (default: false)
 * @param {boolean} cancelLoading - Show loading spinner on cancel button (default: false)
 * @param {...any} dialogProps - Any additional props to pass to the MUI Dialog component
 */
const GenericDialog = ({
  open,
  onClose,
  title = 'Confirm Action',
  content = 'Are you sure you want to proceed?',
  cancelText = 'Cancel',
  confirmText = 'Submit',
  onCancel,
  onConfirm,
  confirmColor = 'primary',
  showCloseIcon = true,
  showDialogActions = true,
  showDialogTitle = true,
  maxWidth = 'sm',
  fullWidth = true,
  disableConfirm = false,
  disableCancel = false,
  hideCancel = false,
  hideConfirm = false,
  confirmLoading = false,
  cancelLoading = false,
  submitLoading = false,
  ...dialogProps
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      {...dialogProps}
    >
      {showDialogTitle && (
        <DialogTitle sx={{ m: 0, p: 2, pr: showCloseIcon ? 6 : 2 }}>
          {title}
          {showCloseIcon && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              disabled={confirmLoading || cancelLoading}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent dividers>
        {typeof content === 'string' ? <DialogContentText>{content}</DialogContentText> : content}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={{ p: 2 }}>
          {!hideCancel && (
            <Button
              onClick={handleCancel}
              disabled={disableCancel || cancelLoading || confirmLoading}
              variant="outlined"
              startIcon={cancelLoading ? <CircularProgress size={16} /> : null}
            >
              {cancelText}
            </Button>
          )}
          {!hideConfirm && (
            <Button
              onClick={handleConfirm}
              disabled={disableConfirm || confirmLoading || cancelLoading}
              variant="contained"
              color={confirmColor}
              startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {confirmText}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GenericDialog;
